// components/shared/ChangePasswordDialog.js
// Dialog לשינוי סיסמה — נפתח מ-Header (default password warning) או מ-Settings
//
// API: POST /auth/change-password
// Body: { old_password, new_password, new_password_secondary? }
// ולידציה: 8+ תווים, אות גדולה, אות קטנה, ספרה, תו מיוחד
// Super Admin: צריך סיסמה שנייה שונה מהראשונה

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword } from '../../utils/passwordValidation';

// =============================================================================
// ChangePasswordDialog
// =============================================================================
export default function ChangePasswordDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { isSuperAdmin, changePassword } = useAuth();

  // Form state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showNew2, setShowNew2] = useState(false);

  // Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const timerRef = useRef(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Validation
  const validation = useMemo(() => validatePassword(newPassword), [newPassword]);
  const allValid = validation.every((r) => r.passed);

  const canSubmit = oldPassword && newPassword && allValid
    && (!isSuperAdmin || (newPassword2 && newPassword !== newPassword2));

  // Reset form
  const resetForm = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOldPassword('');
    setNewPassword('');
    setNewPassword2('');
    setShowOld(false);
    setShowNew(false);
    setShowNew2(false);
    setError('');
    setSuccess(false);
    setLoading(false);
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    if (loading) return;
    resetForm();
    onClose();
  }, [loading, resetForm, onClose]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    setError('');

    const result = await changePassword(
      oldPassword,
      newPassword,
      isSuperAdmin ? newPassword2 : null
    );

    setLoading(false);

    if (result.success) {
      setSuccess(true);
      // Auto-close after 2 seconds
      timerRef.current = setTimeout(() => {
        handleClose();
      }, 2000);
    } else {
      setError(result.error || t('common.error'));
    }
  }, [canSubmit, loading, oldPassword, newPassword, newPassword2, isSuperAdmin, changePassword, t, handleClose]);

  // Handle Enter key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && canSubmit) {
      handleSubmit();
    }
  }, [canSubmit, handleSubmit]);

  // Password visibility toggle
  const visibilityAdornment = (show, setShow) => (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => setShow((p) => !p)}
        edge="end"
        aria-label={show ? t('common.hidePassword') : t('common.showPassword')}
      >
        {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="change-password__dialog"
    >
      {loading && <LinearProgress />}

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {t('users.changePassword')}
        <IconButton aria-label={t('common.close')} size="small" onClick={handleClose} disabled={loading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Success message */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} data-testid="change-password__alert--success">
            {t('users.passwordChanged')}
          </Alert>
        )}

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="change-password__alert--error">
            {error}
          </Alert>
        )}

        {!success && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Old password */}
            <TextField
              data-testid="change-password__input--old-password"
              label={t('users.oldPassword')}
              type={showOld ? 'text' : 'password'}
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              autoFocus
              disabled={loading}
              InputProps={{ endAdornment: visibilityAdornment(showOld, setShowOld) }}
            />

            {/* New password */}
            <TextField
              data-testid="change-password__input--new-password"
              label={t('users.newPassword')}
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
              disabled={loading}
              InputProps={{ endAdornment: visibilityAdornment(showNew, setShowNew) }}
            />

            {/* Validation rules */}
            {newPassword && (
              <List dense disablePadding sx={{ ml: 1 }}>
                {validation.map((rule) => (
                  <ListItem key={rule.key} disableGutters sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {rule.passed
                        ? <CheckCircleIcon fontSize="small" color="success" />
                        : <CancelIcon fontSize="small" color="error" />
                      }
                    </ListItemIcon>
                    <ListItemText
                      primary={t(`users.passwordValidation.${rule.key}`)}
                      primaryTypographyProps={{
                        fontSize: '0.8rem',
                        color: rule.passed ? 'success.main' : 'error.main',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            {/* Super Admin: second password */}
            {isSuperAdmin && (
              <>
                <TextField
                  data-testid="change-password__input--new-password-secondary"
                  label={t('users.password2')}
                  type={showNew2 ? 'text' : 'password'}
                  value={newPassword2}
                  onChange={(e) => setNewPassword2(e.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  disabled={loading}
                  InputProps={{ endAdornment: visibilityAdornment(showNew2, setShowNew2) }}
                />

                {/* Warning: passwords must differ */}
                {newPassword && newPassword2 && newPassword === newPassword2 && (
                  <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                    {t('users.passwordValidation.mustDiffer')}
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button data-testid="change-password__button--cancel" onClick={handleClose} disabled={loading}>
          {success ? t('common.close') : t('common.cancel')}
        </Button>
        {!success && (
          <Button
            data-testid="change-password__button--submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
          >
            {loading ? t('common.saving') : t('users.changePassword')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
